// ------------------------------------------------------------
// Producery – Wiki Pages (Cloudscape + React Router v6)
// ------------------------------------------------------------
// WikiNew.tsx
// ------------------------------------------------------------
// Description: Allows users to create a new wiki page.
// ------------------------------------------------------------
// Copyright (c) 2025 The Horrible Meadery
// Licensed under the MIT License
// ------------------------------------------------------------

import {
  Alert,
  Box,
  Button,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { WikiPageCreate } from '../../models/Wiki';
import { createWikiPage, deriveHierarchy, getWikiPage } from './Wiki.logic';
import { statusOptions, useSession, visibilityOptions } from './Wiki.shared';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const WikiNew = () => {
  const { tenantId, userId } = useSession();
  const navigate = useNavigate();
  const q = useQuery();
  const parent = q.get('parent') || '';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState(parent ? `${parent}-` : '');
  const [slugDirty, setSlugDirty] = useState(Boolean(parent));
  const [form, setForm] = useState<WikiPageCreate>({
    title: '',
    contentMd: '',
    visibility: 'private',
    status: 'draft',
    tags: [],
    categories: [],
    pinned: false,
  } as any);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugExists, setSlugExists] = useState(false);
  const [checking, setChecking] = useState(false);

  // Auto-build slug from Title unless user has typed in Slug manually
  useEffect(() => {
    if (!slugDirty) {
      const base = slugify(title);
      setSlug(parent ? (base ? `${parent}-${base}` : `${parent}-`) : base);
    }
  }, [title, parent, slugDirty]);

  // Debounced uniqueness check for slug
  const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!slug) {
      setSlugExists(false);
      return;
    }
    if (!slugRegex.test(slug)) {
      setSlugExists(false);
      return;
    }
    setChecking(true);
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      const existing = await getWikiPage(tenantId, slug);
      setSlugExists(Boolean(existing));
      setChecking(false);
    }, 300);
    return () => {
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [tenantId, slug]);

  const hierarchy = deriveHierarchy(slug);

  const slugError = !slug
    ? 'Slug is required'
    : !slugRegex.test(slug)
      ? 'Lowercase letters, numbers and hyphens only (no leading/trailing hyphen).'
      : parent && !slug.startsWith(parent + '-')
        ? `This should start with "${parent}-" to be a subpage.`
        : slugExists
          ? 'A page with this slug already exists.'
          : null;

  async function onCreate() {
    try {
      setSaving(true);
      await createWikiPage(tenantId, slug, { ...form, title }, userId);
      navigate(`/wiki/${slug}`);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  // ==== Multiselect state (creatable) for Tags & Categories ====
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [tagFilter, setTagFilter] = useState('');
  const selectedTagOptions = useMemo(() => (form.tags || []).map((v) => ({ label: v, value: v })), [form.tags]);
  const computedTagOptions = useMemo(() => {
    const baseVals = new Set([...(form.tags || []), ...tagOptions.map((o) => o.value)]);
    const base = Array.from(baseVals).map((v) => ({ label: v, value: v }));
    if (tagFilter && !Array.from(baseVals).some((v) => v.toLowerCase() === tagFilter.toLowerCase())) {
      base.unshift({ label: `Create "${tagFilter}"`, value: `__new__:${tagFilter}` });
    }
    return base;
  }, [tagOptions, tagFilter, form.tags]);

  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const selectedCategoryOptions = useMemo(
    () => (form.categories || []).map((v) => ({ label: v, value: v })),
    [form.categories],
  );
  const computedCategoryOptions = useMemo(() => {
    const baseVals = new Set([...(form.categories || []), ...categoryOptions.map((o) => o.value)]);
    const base = Array.from(baseVals).map((v) => ({ label: v, value: v }));
    if (categoryFilter && !Array.from(baseVals).some((v) => v.toLowerCase() === categoryFilter.toLowerCase())) {
      base.unshift({ label: `Create "${categoryFilter}"`, value: `__new__:${categoryFilter}` });
    }
    return base;
  }, [categoryOptions, categoryFilter, form.categories]);

  return (
    <ContentLayout disableOverlap header={<Header variant="h1">New Wiki Page</Header>}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={!title || !slug || !!slugError || checking}
              onClick={onCreate}
            >
              Create
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          {error && (
            <Alert type="error" header="Error">
              {error}
            </Alert>
          )}

          <FormField label="Title">
            <Input
              value={title}
              onChange={(e) => {
                setTitle(e.detail.value);
                setForm((f) => ({ ...f, title: e.detail.value }));
              }}
            />
          </FormField>

          <FormField
            label="Slug"
            errorText={slugError || undefined}
            description={
              <Box variant="p" color="text-body-secondary">
                Top-level if no hyphen. Parent: {hierarchy.parentSlug || '—'}; Level: {hierarchy.level}
              </Box>
            }
          >
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.detail.value);
                setSlugDirty(true);
              }}
              placeholder={parent ? `${parent}-new-page` : 'e.g. agp-excise'}
            />
          </FormField>

          <FormField label="Visibility">
            <Select
              selectedOption={{
                value: form.visibility,
                label: visibilityOptions.find((o) => o.value === form.visibility)?.label || '',
              }}
              onChange={(e) => setForm((f) => ({ ...f, visibility: e.detail.selectedOption.value as any }))}
              options={visibilityOptions}
            />
          </FormField>

          <FormField label="Status">
            <Select
              selectedOption={{
                value: form.status,
                label: statusOptions.find((o) => o.value === form.status)?.label || '',
              }}
              onChange={(e) => setForm((f) => ({ ...f, status: e.detail.selectedOption.value as any }))}
              options={statusOptions}
            />
          </FormField>

          <FormField label="Tags">
            <Multiselect
              selectedOptions={selectedTagOptions}
              options={computedTagOptions}
              placeholder="Add tags…"
              filteringType="auto"
              onLoadItems={({ detail }) => setTagFilter(detail.filteringText ?? '')}
              onChange={({ detail }) => {
                let vals = detail.selectedOptions.map((o) => String(o.value));
                const created = vals.filter((v) => v.startsWith('__new__:'));
                if (created.length) {
                  const pure = created.map((v) => v.slice(8));
                  setTagOptions((opts) => {
                    const current = new Set(opts.map((o) => o.value));
                    pure.forEach((v) => current.add(v));
                    return Array.from(current).map((v) => ({ label: v, value: v }));
                  });
                  vals = vals.map((v) => (v.startsWith('__new__:') ? v.slice(8) : v));
                }
                setForm((f) => ({ ...f, tags: vals }));
              }}
            />
          </FormField>

          <FormField label="Categories">
            <Multiselect
              selectedOptions={selectedCategoryOptions}
              options={computedCategoryOptions}
              placeholder="Add categories…"
              filteringType="auto"
              onLoadItems={({ detail }) => setCategoryFilter(detail.filteringText ?? '')}
              onChange={({ detail }) => {
                let vals = detail.selectedOptions.map((o) => String(o.value));
                const created = vals.filter((v) => v.startsWith('__new__:'));
                if (created.length) {
                  const pure = created.map((v) => v.slice(8));
                  setCategoryOptions((opts) => {
                    const current = new Set(opts.map((o) => o.value));
                    pure.forEach((v) => current.add(v));
                    return Array.from(current).map((v) => ({ label: v, value: v }));
                  });
                  vals = vals.map((v) => (v.startsWith('__new__:') ? v.slice(8) : v));
                }
                setForm((f) => ({ ...f, categories: vals }));
              }}
            />
          </FormField>

          <FormField label="Content (Markdown)">
            <div data-color-mode="light">
              <MDEditor
                value={form.contentMd}
                onChange={(v) => setForm((f) => ({ ...f, contentMd: v || '' }))}
                preview="live" // edit | preview | live
                height={420}
                textareaProps={{ placeholder: '# Heading\n\nStart writing…' }}
                previewOptions={{
                  remarkPlugins: [remarkGfm],
                  rehypePlugins: [rehypeSanitize],
                }}
              />
            </div>
          </FormField>
        </SpaceBetween>
      </Form>
    </ContentLayout>
  );
};

export default WikiNew;
